import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Services', href: '#services' },
    { name: 'Case Study', href: '#case-study' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Why Us', href: '#why-us' },
    { name: 'Our Process', href: '#process' },
    { name: 'Blog', href: '#blog' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const offset = 80; // height of fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'glass-nav py-4 shadow-md shadow-[#3E2F24]/5' 
            : 'bg-transparent py-6'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2.5 cursor-pointer group"
              data-testid="logo-link"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-tr from-[#8B6B4A] via-[#C8A97E] to-[#D6C2A8] flex items-center justify-center shadow-md transition-all duration-300">
                <span className="font-space font-bold text-white text-base">CC</span>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[#3E2F24] font-space" data-testid="company-logo">
                Code<span className="text-[#8B6B4A] font-medium">Credence</span>
              </h1>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="text-[#6B625B] hover:text-[#3E2F24] font-medium transition-colors text-sm font-poppins relative py-2 group"
                  data-testid={`nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B6B4A] group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
              
              {/* Call to Action Button */}
              <button
                onClick={() => scrollToSection('#contact')}
                className="relative group overflow-hidden bg-[#3E2F24] hover:bg-[#8B6B4A] text-white rounded-full px-6 py-2.5 text-sm font-medium font-poppins transition-all duration-300 shadow-sm active:scale-95 flex items-center gap-2"
                data-testid="header-contact-btn"
              >
                <span className="relative z-10 flex items-center gap-1.5">
                  Let's Talk
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-[#8B6B4A] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></span>
              </button>
            </nav>

            {/* Mobile Menu Toggle Button */}
            <button
              className="lg:hidden text-[#3E2F24] hover:text-[#8B6B4A] p-2 rounded-xl bg-[#3E2F24]/5 border border-[#3E2F24]/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-[#3E2F24] z-40 lg:hidden"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-4/5 max-w-sm z-50 lg:hidden bg-[#F8F5F0]/98 backdrop-blur-xl border-l border-[#3E2F24]/10 p-8 flex flex-col justify-between"
              data-testid="mobile-menu"
            >
              <div className="space-y-8">
                {/* Header inside menu */}
                <div className="flex items-center justify-between pb-6 border-b border-[#3E2F24]/10">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-[#8B6B4A] via-[#C8A97E] to-[#D6C2A8] flex items-center justify-center shadow-sm">
                      <span className="font-space font-bold text-white text-sm">CC</span>
                    </div>
                    <span className="font-space font-bold text-[#3E2F24]">CodeCredence</span>
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-[#6B625B] hover:text-[#3E2F24] p-2 rounded-lg bg-[#3E2F24]/5"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Nav links */}
                <div className="flex flex-col gap-6">
                  {navItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => scrollToSection(item.href)}
                      className="text-left text-lg text-[#3E2F24] hover:text-[#8B6B4A] font-medium font-poppins transition-colors py-1 border-b border-[#3E2F24]/5"
                      data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bottom Call to Action */}
              <div className="space-y-4">
                <button
                  onClick={() => scrollToSection('#contact')}
                  className="w-full bg-[#3E2F24] hover:bg-[#8B6B4A] text-white rounded-full py-4 text-center font-medium font-poppins flex items-center justify-center gap-2 transition-all duration-300 shadow-sm"
                  data-testid="mobile-contact-btn"
                >
                  Get a Proposal
                  <ArrowUpRight className="w-5 h-5" />
                </button>
                <p className="text-center text-xs text-[#6B625B] font-poppins">
                  © {new Date().getFullYear()} CodeCredence
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
